import type { JSX } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './ConnectionBanner.css';

/**
 * The connection-lost banner, lifted out of the lobby into shared/components so
 * every phase (Lobby, Ghost meeting, clue-giving, future reveal) gets the same
 * transient disconnect treatment without re-implementing it. Terminal failures
 * — schema mismatch, room full, boot errors — are a separate concern, the
 * full-screen `ClosedDoorCard`.
 *
 * Rendering happens at the App level (RealAppInner in App.tsx, MockApp.tsx in
 * dev-mock). Phase view types don't carry a `connection` field; the App reads
 * it from the LobbySession snapshot and decides what to render around the
 * active phase.
 */
export function ConnectionBanner({ visible }: { visible: boolean }): JSX.Element | null {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <motion.div
      className="susdle-banner"
      role="status"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="susdle-banner__dot" aria-hidden />
      <span className="susdle-banner__text">{t('errors.connectionLost')}</span>
    </motion.div>
  );
}
